import { GAME_MODE, milliseconds, NotesSection, seconds, SingSetup, Song, songBeat } from '~/interfaces';
import {
  addDetailedScores,
  beatsToPoints,
  divideDetailedScores,
  emptyDetailedScore,
} from '~/modules/game-engine/game-state/helpers/calculate-score';
import getCurrentBeat from '~/modules/game-engine/game-state/helpers/get-current-beat';
import { getSingerAtSection } from '~/modules/game-engine/game-state/helpers/turns';
import PlayerState from '~/modules/game-engine/game-state/player-state';
import InputManager from '~/modules/game-engine/input/input-manager';
import { PlayerNumber } from '~/modules/players/player-number';
import getSongBeatCount from '~/modules/songs/utils/get-song-beat-count';
import getSongBeatLength from '~/modules/songs/utils/get-song-beat-length';
import tuple from '~/modules/utils/tuple';

export class GameStateClass {
  private song: Song | null = null;
  private currentTime: milliseconds = 0;
  private duration: seconds = 0;
  private singSetup: SingSetup | null = null;
  private playerStates: PlayerState[] = [];
  private playing: boolean = false;

  public setCurrentTime = (currentTime: milliseconds) => (this.currentTime = currentTime);
  public getCurrentTime = (accountGap = true): milliseconds => {
    return this.currentTime - (accountGap && this.song ? this.song.gap : 0);
  };

  public getSongBeatLength = (): milliseconds => getSongBeatLength(this.song!);
  public getCurrentBeat = (): songBeat => {
    return getCurrentBeat(this.getCurrentTime(), this.getSongBeatLength(), 0, false);
  };

  public setSong = (song: Song) => (this.song = song);
  public getSong = () => this.song;

  public getSingSetup = () => this.singSetup;
  public setSingSetup = (singSetup: SingSetup) => {
    this.singSetup = singSetup;

    this.playerStates = singSetup.players.map(({ number }) => new PlayerState(number, this));
    this.currentTime = 0;
    this.playing = true;
  };

  public resetSingSetup = () => {
    this.playing = false;
  };

  public isPlaying = () => this.playing;

  public getTolerance = () => this.getSingSetup()?.tolerance ?? 2;

  public setDuration = (duration: seconds) => (this.duration = duration);
  public getDuration = () => this.duration;

  public getPlayer = (playerNumber: PlayerNumber) =>
    this.playerStates.find((player) => player.getNumber() === playerNumber);

  public getPlayers = () => this.playerStates;

  public getPlayerCount = () => this.playerStates.length;

  public getPlayerScore = (player: PlayerNumber) => {
    if (this.getSingSetup()?.mode === GAME_MODE.CO_OP) {
      const score = this.getPlayers().reduce((curr, playerState) => curr + playerState.getScore(), 0);

      return score / this.getPlayerCount();
    } else {
      // Zero rather than -1 for anyone who joined mid-song and so has nothing to score: nothing
      // treats -1 as "no score", so it reached the lane display, the stored play, the profile and
      // the leaderboard submission as a negative score.
      return this.getPlayer(player)?.getScore() ?? 0;
    }
  };

  public getPlayerDetailedScore = (player: PlayerNumber) => {
    if (this.getSingSetup()?.mode === GAME_MODE.CO_OP) {
      const scores = this.getPlayers().map((playerState) => playerState.getDetailedScore());

      // getDetailedScore returns beats - calculate actual points for each player
      const [fs, ...pointsScores] = scores.map(
        ([pointsPerBeat, counts, maxCounts]) =>
          [beatsToPoints(counts, pointsPerBeat), beatsToPoints(maxCounts, pointsPerBeat)] as const,
      );

      const summedPoints = pointsScores.reduce(
        (curr, pointsScore) => [addDetailedScores(curr[0], pointsScore[0]), addDetailedScores(curr[1], pointsScore[1])],
        fs,
      );
      return tuple([
        divideDetailedScores(summedPoints[0], this.getPlayerCount()),
        divideDetailedScores(summedPoints[1], this.getPlayerCount()),
      ]);
    } else {
      const playerState = this.getPlayer(player);

      // playerStates only covers the players the song started with, while callers iterate the live
      // PlayersManager roster - anyone who joined mid-song has nothing to score. `getPlayerScore`
      // above already tolerates this; the non-null assertion here used to throw on the results
      // screen instead.
      if (!playerState) {
        return tuple([emptyDetailedScore(), emptyDetailedScore()]);
      }

      const [pointsPerBeat, counts, maxCounts] = playerState.getDetailedScore();

      return tuple([beatsToPoints(counts, pointsPerBeat), beatsToPoints(maxCounts, pointsPerBeat)]);
    }
  };

  public getSongCompletionProgress = () => {
    return Math.max(0, Math.min(1, this.getCurrentBeat() / getSongBeatCount(this.getSong()!)));
  };

  public startInputMonitoring = async () => {
    return InputManager.startMonitoring();
  };

  public stopInputMonitoring = () => {
    return InputManager.stopMonitoring();
  };

  public update = () => {
    this.playerStates.forEach((player) => player.update());
  };
  public resetPlayerNotes = () => {
    this.playerStates.forEach((player) => player.resetNotes());
  };

  public isMergedTrack = () => this.getSingSetup()?.players.length !== 2;

  /**
   * The singers sharing one track. With any count but two everybody reads the merged track whatever
   * their setup says (`isMergedTrack`), so grouping by the configured index alone would put three
   * singers on three tracks.
   */
  public getTrackSingers = (trackIndex: number): PlayerNumber[] =>
    this.playerStates
      .filter((singer) => this.isMergedTrack() || singer.getTrackIndex() === trackIndex)
      .map((singer) => singer.getNumber());

  /**
   * Which sections of the track this singer is answerable for, or `undefined` when that is all of
   * them - outside pass the mic, and for a singer with nobody to pass to.
   *
   * The one answer to "was this theirs to sing", read by the score, the combo and the note stats
   * alike. Scoring used to be the exception: it measured every singer against the whole track while
   * the other two counted only their own turns, so a flawless turn scored its share of the song -
   * 43% and a grade of D next to an accuracy of 100%.
   *
   * `undefined` rather than a predicate that always returns true, so the common case stays on
   * `calculate-score`'s cached whole-track path.
   */
  public getSectionOwnership = (playerNumber: PlayerNumber): ((section: NotesSection) => boolean) | undefined => {
    if (this.getSingSetup()?.mode !== GAME_MODE.PASS_THE_MIC) return undefined;

    const player = this.getPlayer(playerNumber);
    if (!player) return undefined;

    const singers = this.getTrackSingers(player.getTrackIndex());
    const turn = singers.indexOf(playerNumber);
    if (singers.length < 2 || turn < 0) return undefined;

    const changes = player.getTrack().changes;

    return (section) => getSingerAtSection(changes, section.start, singers.length) === turn;
  };

  public ownsSection = (playerNumber: PlayerNumber, section: NotesSection) =>
    this.getSectionOwnership(playerNumber)?.(section) ?? true;
}

export default new GameStateClass();
